import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import ReactApexChart from "react-apexcharts";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Badge,
  Divider,
  List,
  Spin,
  Button,
  message,
  Tooltip,
  Row,
  Col,
  Radio,
  Select,
} from "antd";
import "../CSS/BlinkingTag.css";
import { fetchMachineById } from "../../../../configuration/module/MachineConfiguration/Service/machine";
import StompSubscriber from "../../../../configuration/module/WebSocket/Component/StompSubscriber";
import { MdFilterListOff } from "react-icons/md";
import { LuMapPinCheck, LuMapPinX } from "react-icons/lu";
import { IoWarningOutline } from "react-icons/io5";
import { fetchHistoricalData } from "../Service/liveMonitoring";
import { CopyOutlined } from "@ant-design/icons";

const LiveMonitoring = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null); // set websocket received data here
  const [loading, setLoading] = useState(false);
  const [machineData, setMachineData] = useState(null);

  const fetchMachineDetails = async () => {
    if (!uuid) return;
    setLoading(true);
    try {
      const res = await fetchMachineById(uuid);
      if (res?.data?.data) {
        setMachineData(res.data.data);
      } else {
        message.error("Invalid response from server");
      }
    } catch (err) {
      message.error("Error fetching machine details");
    } finally {
      setLoading(false);
    }
  };

  // 1️⃣ Current time, data timestamp, and delay
  const currentTime = new Date();
  const dataTimestamp = data?.timestamp
    ? new Date(
        isNaN(data.timestamp)
          ? Date.parse(data.timestamp)
          : data.timestamp * 1000
      )
    : null;

  const delaySeconds = dataTimestamp
    ? Math.floor((currentTime - dataTimestamp) / 1000)
    : null;

  // 2️⃣ Extract bottom-most children
  // Parse sampleJson safely
  let parsedSampleJson = {};
  try {
    parsedSampleJson =
      typeof machineData?.sampleJson === "string"
        ? JSON.parse(machineData.sampleJson)
        : machineData?.sampleJson || {};
  } catch (err) {
    parsedSampleJson = {};
  }

  // 2️⃣ Extract bottom-most children
  const leafNodes = getLeafNodes(parsedSampleJson);
  const bottomMostCount = leafNodes.length;

  // 3️⃣ MachineData tag count
  const tagCount = machineData?.tags?.length || 0;

  // 4️⃣ Difference
  const missingCount = Math.max(bottomMostCount - tagCount, 0);

  // Extract all bottom-most children (leaf nodes) with their key paths
  function getLeafNodes(obj, prefix = "") {
    let leaves = [];
    if (typeof obj !== "object" || obj === null) return [];

    for (const [key, value] of Object.entries(obj)) {
      const path = prefix ? `${prefix}.${key}` : key;
      if (typeof value === "object" && value !== null) {
        leaves = leaves.concat(getLeafNodes(value, path));
      } else {
        leaves.push({ key: path, value });
      }
    }
    return leaves;
  }

  useEffect(() => {
    fetchMachineDetails();
  }, [uuid]);

  const handleMessage = useCallback((msg) => {
    setData(msg);
  }, []);

  function convertTimestampToDateTime(timestamp, timeZone = "Asia/Kolkata") {
    // Convert seconds → milliseconds
    const date = new Date(timestamp * 1000);
    // Format it to readable date & time
    return date.toLocaleString("en-IN", {
      timeZone,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  }

  if (loading)
    return (
      <div style={{ textAlign: "center", padding: "100px 0" }}>
        <Spin size="large" tip="Loading machine details..." />
      </div>
    );

  if (!machineData)
    return (
      <div style={{ textAlign: "center", padding: 100 }}>
        <p>No machine data found.</p>
        <Button type="primary" onClick={() => navigate(-1)}>
          Back
        </Button>
      </div>
    );

  function transformTimestamps(obj) {
    if (typeof obj !== "object" || obj === null) return obj;

    if (Array.isArray(obj)) {
      return obj.map(transformTimestamps);
    }

    const newObj = {};
    for (const [key, value] of Object.entries(obj)) {
      // Detect typical timestamp fields
      if (
        (key.toLowerCase().includes("time") ||
          key.toLowerCase().includes("timestamp")) &&
        typeof value === "number" &&
        value > 1000000000 // rough threshold for Unix timestamps
      ) {
        newObj[key] = `${convertTimestampToDateTime(value)}`;
      } else {
        newObj[key] = transformTimestamps(value);
      }
    }
    return newObj;
  }

  const formattedData = data ? transformTimestamps(data) : null;

  return (
    <div>
      <Card
        title={
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <span>Machine Details</span>
            <Button onClick={() => navigate(-1)}>Back</Button>
          </div>
        }
      >
        <StompSubscriber asset={machineData} onMessage={handleMessage} />

        <Descriptions bordered column={2} size="middle">
          <Descriptions.Item label="Machine Name">
            {machineData.assetName || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Organization">
            {machineData.firm?.name || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Current Time">
            {currentTime.toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Data Timestamp">
            {dataTimestamp ? dataTimestamp.toLocaleString() : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Delay">
            {delaySeconds !== null
              ? delaySeconds < 60
                ? `${delaySeconds} sec`
                : delaySeconds < 3600
                ? `${(delaySeconds / 60).toFixed(1)} min`
                : `${(delaySeconds / 3600).toFixed(1)} hr`
              : "—"}
          </Descriptions.Item>
          <Descriptions.Item label="Total Parameter">
            {bottomMostCount}
          </Descriptions.Item>
          <Descriptions.Item label="Mapped Parameter">
            {tagCount}
          </Descriptions.Item>
          <Descriptions.Item label="Unmapped Parameter">
            {missingCount}
          </Descriptions.Item>
          <Descriptions.Item label="Machine Status">
            {machineData.status === true ? (
              <Badge status="success" text="Active" />
            ) : (
              <Badge status="error" text="Inactive" />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Connection Status">
            {machineData.connectionStatus === true ? (
              <Badge status="success" text="Connected" />
            ) : (
              <Badge status="error" text="Disconnected" />
            )}
          </Descriptions.Item>
        </Descriptions>

        <Divider />
        <h3>Parameter Locations</h3>
        <br />
        {!machineData.images?.length ? (
          <p>No images uploaded.</p>
        ) : (
          machineData.images.map((src, imgIndex) => (
            <ImageWithGuidedTags
              data={data}
              key={imgIndex}
              src={src}
              imgIndex={imgIndex}
              tags={machineData.tags || []}
            />
          ))
        )}
        <Divider />
        {/* <Card title="Live Data" size="small" style={{ marginBottom: 16 }}>
          <pre
            style={{
              background: "#f7f7f7",
              padding: 10,
              borderRadius: 6,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            {formattedData
              ? JSON.stringify(formattedData, null, 2)
              : "(No JSON data)"}
          </pre>
        </Card> */}

        <Card
          title="Live Data"
          size="small"
          style={{ marginBottom: 16 }}
          extra={
            <Tooltip title="Copy JSON">
              <Button
                size="small"
                icon={<CopyOutlined />}
                onClick={() => {
                  if (!formattedData) {
                    message.warning("No data to copy");
                    return;
                  }

                  navigator.clipboard
                    .writeText(JSON.stringify(formattedData, null, 2))
                    .then(() => message.success("JSON copied to clipboard"))
                    .catch(() => message.error("Failed to copy"));
                }}
              />
            </Tooltip>
          }
        >
          <pre
            style={{
              background: "#f7f7f7",
              padding: 10,
              borderRadius: 6,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            {formattedData
              ? JSON.stringify(formattedData, null, 2)
              : "(No JSON data)"}
          </pre>
        </Card>

        <Divider />
        <GraphArea
          data={data}
          assetUuid={machineData.uuid}
          leafNodes={leafNodes}
          tags={machineData.tags || []}
        />
      </Card>
    </div>
  );
};

/* --------------------------------------------
   Helper component: Image with guided lines + tags
--------------------------------------------- */
const ImageWithGuidedTags = ({ src, imgIndex, tags, data }) => {
  const [showTags, setShowTags] = useState(false);
  const imgRef = useRef(null);
  const [dimensions, setDimensions] = useState({ width: 1, height: 1 });
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setDimensions({
        width: rect.width,
        height: rect.height,
      });
    });

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const handleImageLoad = () => {
    setTimeout(() => {
      if (imgRef.current) {
        const rect = imgRef.current.getBoundingClientRect();
        setDimensions({ width: rect.width, height: rect.height });
        setShowTags(true);
      }
    }, 200);
  };

  const getValueFromPath = (obj, path) => {
    const value = path.split(".").reduce((acc, part) => acc && acc[part], obj);
    if (value === undefined || value === null) return "-";
    // Convert objects/arrays to readable JSON
    return typeof value === "object" ? JSON.stringify(value) : value;
  };

  const isConditionMet = (value, condition) => {
    const val =
      typeof value === "string" && !isNaN(value) ? Number(value) : value;
    const condVal =
      typeof condition.value === "string" && !isNaN(condition.value)
        ? Number(condition.value)
        : condition.value;

    switch (condition.operator) {
      case "==":
        return val === condVal;
      case "!=":
        return val !== condVal;
      case ">":
        return val > condVal;
      case "<":
        return val < condVal;
      case ">=":
        return val >= condVal;
      case "<=":
        return val <= condVal;
      default:
        return false;
    }
  };

  return (
    // <div
    //   style={{
    //     border: "1px solid #ddd",
    //     marginTop: 16,
    //     borderRadius: 8,
    //     background: "#fafafa",
    //     position: "relative",
    //   }}
    // >
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "50%", // initial width
        resize: "horizontal", // drag handle
        overflow: "hidden",
        border: "1px solid #ddd",
        borderRadius: 8,
        background: "#fafafa",
      }}
    >
      {/* <img
        ref={imgRef}
        src={src}
        alt={`machine-img-${imgIndex}`}
        style={{ width: "100%", height: "auto", borderRadius: 6 }}
        onLoad={handleImageLoad}
      /> */}
      <img
        ref={imgRef}
        src={src}
        alt={`machine-img-${imgIndex}`}
        style={{
          width: "100%",
          height: "auto", // 🔥 keeps ratio
          display: "block",
          borderRadius: 6,
        }}
        onLoad={handleImageLoad}
      />

      {showTags && (
        // <svg
        //   style={{
        //     position: "absolute",
        //     top: 0,
        //     left: 0,
        //     width: "100%",
        //     height: "100%",
        //     pointerEvents: "none",
        //   }}
        // >
        <svg
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
        >
          {tags
            .filter((t) => t.imgIndex === imgIndex)
            .map((tag, idx) => {
              const { width, height } = dimensions;
              const anchorX = (tag.anchorXPercent / 100) * width;
              const anchorY = (tag.anchorYPercent / 100) * height;
              const x = (tag.xPercent / 100) * width;
              const y = (tag.yPercent / 100) * height;
              return (
                <line
                  key={idx}
                  x1={anchorX}
                  y1={anchorY}
                  x2={x}
                  y2={y}
                  stroke="red"
                  strokeWidth="2"
                />
              );
            })}
        </svg>
      )}

      {showTags &&
        tags
          .filter((t) => t.imgIndex === imgIndex)
          .map((tag, idx) => {
            const value = getValueFromPath(data, tag.key);

            // Check if any condition is met
            const activeConditions =
              tag.conditions?.filter((cond) => isConditionMet(value, cond)) ||
              [];

            const shouldBlink = activeConditions.length > 0;

            return (
              <Tooltip title="Click to view graph" key={idx}>
                <div
                  key={idx}
                  style={{
                    position: "absolute",
                    top: `${tag.yPercent}%`,
                    left: `${tag.xPercent}%`,
                    transform: "translate(-10%, -10%)",
                    // background: "white",
                    background: "rgba(255, 255, 255, 0.8)",
                    border: "1px solid #ccc",
                    borderRadius: 6,
                    padding: 6,
                    animation: shouldBlink ? "blinkShadow 1s infinite" : "none",
                    boxShadow: shouldBlink ? "none" : "black 0px 0px 30px",
                  }}
                  onClick={() => console.log("Button will come")}
                >
                  <h4>
                    {tag.displayName} : {value}
                  </h4>
                  {activeConditions.map((cond, cIdx) => (
                    <div
                      key={cIdx}
                      style={{
                        fontWeight: "bold",
                        width: "100%",
                        textAlign: "center",
                        margin: 0,
                      }}
                    >
                      {cond.message || "Alert"}
                    </div>
                  ))}
                </div>
              </Tooltip>
            );
          })}
    </div>
  );
};

/* --------------------------------------------
   Helper component: Historical data graph area
--------------------------------------------- */

const ONE_MIN_PAGE_MS = 1 * 60 * 1000;

const formatWindowLabel = (aggType, start, end) => {
  if (aggType === "LIVE") return "Live Data (Current)";
  if (!start || !end) return "";

  const s = new Date(start);
  const e = new Date(end);

  switch (aggType) {
    case "YEAR":
      return `${s.toLocaleString("default", {
        month: "short",
        year: "numeric",
      })} → ${e.toLocaleString("default", {
        month: "short",
        year: "numeric",
      })}`;

    case "MONTH":
      return `${s.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })} → ${e.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })}`;

    case "DAY":
    case "HOUR":
    // case "QUARTER":
    case "RAW":
      return `${s.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })} → ${e.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      })}`;

    // case "LIVE":
    //   return "Live Data (Current)";
    case "LIVE": {
      const now = new Date();

      const dateStr = now.toLocaleDateString("en-IN", {
        year: "numeric",
        month: "short",
        day: "2-digit",
      });

      const timeStr = now.toLocaleTimeString("en-IN", {
        hour12: false,
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });

      // ✅ Extract current live value
      let liveValue = "-";
      if (start && typeof start === "object" && start.liveValue !== undefined) {
        liveValue = start.liveValue;
      }

      return `Live Data (Current) — ${dateStr} ${timeStr} — Value: ${liveValue}`;
    }

    default:
      return "";
  }
};

const toISOStringTrimmed = (d) => new Date(d).toISOString();

// const startOfToday = (d = new Date()) => new Date(d.setHours(0, 0, 0, 0));
const startOfToday = (d = new Date()) => {
  const copy = new Date(d); // make a copy
  copy.setHours(0, 0, 0, 0);
  return copy;
};

const startOfMonth = (d = new Date()) =>
  new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const startOfYear = (d = new Date()) =>
  new Date(d.getFullYear(), 0, 1, 0, 0, 0, 0);
const endOfMonth = (d) => {
  const e = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  e.setHours(23, 59, 59, 999);
  return e;
};
const endOfYear = (d) => {
  const e = new Date(d.getFullYear(), 11, 31);
  e.setHours(23, 59, 59, 999);
  return e;
};

const { Option } = Select;

const GraphArea = ({ data, leafNodes, tags = [], assetUuid }) => {
  const [selectedKey, setSelectedKey] = useState(null);
  const [filterType, setFilterType] = useState("All");
  const [aggregationType, setAggregationType] = useState("LIVE");
  const [chartData, setChartData] = useState([]);
  const [pageWindow, setPageWindow] = useState({ start: null, end: null });
  const [isLoading, setIsLoading] = useState(false);
  const rawInitialRef = useRef({ start: null, end: null });
  const [liveSeries, setLiveSeries] = useState([]);
  const [selectorOptions, setSelectorOptions] = useState([]); // { value, label, disabled }
  const [selectorValue, setSelectorValue] = useState(null);

  const generateTimeSlots = (aggType, start, end) => {
    const slots = [];
    const s = new Date(start);
    const e = new Date(end);

    // ✅ Normalize start to clean boundary
    if (aggType === "DAY") s.setHours(0, 0, 0, 0);
    if (aggType === "HOUR") {
      // normalize to nearest lower 15-min mark
      const minutes = s.getMinutes();
      s.setMinutes(Math.floor(minutes / 15) * 15, 0, 0);
    }
    if (aggType === "MONTH") s.setHours(0, 0, 0, 0);
    if (aggType === "YEAR") s.setMonth(0, 1, 0, 0, 0, 0, 0);

    switch (aggType) {
      case "YEAR":
        for (let i = 0; i < 12; i++) {
          slots.push({ x: new Date(s.getFullYear(), i, 1), y: null });
        }
        break;

      case "MONTH": {
        const daysInMonth = new Date(
          s.getFullYear(),
          s.getMonth() + 1,
          0
        ).getDate();
        for (let i = 1; i <= daysInMonth; i++) {
          slots.push({
            x: new Date(s.getFullYear(), s.getMonth(), i),
            y: null,
          });
        }
        break;
      }

      case "DAY":
        for (let i = 0; i < 24; i++) {
          const label = new Date(s);
          label.setHours(i, 0, 0, 0);
          slots.push({ x: label, y: null });
        }
        break;

      case "HOUR":
        // ✅ Always generate from normalized start, every 15 minutes
        for (let i = 0; i < 60; i += 15) {
          const label = new Date(s);
          label.setMinutes(s.getMinutes() + i, 0, 0);
          slots.push({ x: label, y: null });
        }
        break;

      case "RAW":
        for (let i = 0; i < 60; i += 5) {
          const label = new Date(s);
          label.setSeconds(i, 0);
          slots.push({ x: label, y: null });
        }
        break;

      default:
        slots.push({ x: new Date(), y: null });
    }

    return slots;
  };

  // helpers: gets real timestamps from aggregated chartData items
  const extractTimestampsFromAggData = (apiData, selectedKey) => {
    if (!apiData || !apiData.length) return [];
    const tsList = [];
    for (const itm of apiData) {
      // aggregated responses in your code store timestamps under highData / lowData
      const h = itm.highData?.[selectedKey]?.timestamp;
      const l = itm.lowData?.[selectedKey]?.timestamp;
      const ts = h ?? l ?? itm.timestamp ?? itm.TimeStamp;
      if (ts) {
        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);
        tsList.push(date);
      }
    }
    return tsList;
  };

  const buildYearOptions = (dates) => {
    // input: array of Date objects
    const presentMonths = new Set(dates.map((d) => d.getMonth()));
    const opts = Array.from({ length: 12 }, (_, i) => {
      const label = new Date(0, i, 1).toLocaleString("en-IN", {
        month: "short",
      });
      return { value: i, label, disabled: !presentMonths.has(i) };
    });
    return opts;
  };

  const buildMonthOptions = (dates, referenceDate) => {
    // show all days in that month; enable if any timestamp exists on that day
    const year = referenceDate.getFullYear();
    const month = referenceDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const presentDays = new Set(dates.map((d) => d.getDate()));
    const opts = Array.from({ length: daysInMonth }, (_, i) => {
      const day = i + 1;
      return {
        value: day,
        label: String(day).padStart(2, "0"),
        disabled: !presentDays.has(day),
      };
    });
    return opts;
  };

  const buildDayOptions = (dates) => {
    // hours 0..23, enable if any timestamp in that hour
    const presentHours = new Set(dates.map((d) => d.getHours()));
    const opts = Array.from({ length: 24 }, (_, i) => {
      const label = `${String(i).padStart(2, "0")}:00–${String(
        (i + 1) % 24
      ).padStart(2, "0")}:00`;
      return { value: i, label, disabled: !presentHours.has(i) };
    });
    return opts;
  };

  const buildHourOptions = (dates, hourStartDate) => {
    // 4 ranges: 00-14,15-29,30-44,45-59 for that hour
    const ranges = [
      { startMin: 0, endMin: 14, label: "00–14" },
      { startMin: 15, endMin: 29, label: "15–29" },
      { startMin: 30, endMin: 44, label: "30–44" },
      { startMin: 45, endMin: 59, label: "45–59" },
    ];
    const opts = ranges.map((r, idx) => {
      const has = dates.some((d) => {
        // ensure same hour and minute in range
        return (
          d.getHours() === hourStartDate.getHours() &&
          d.getFullYear() === hourStartDate.getFullYear() &&
          d.getMonth() === hourStartDate.getMonth() &&
          d.getDate() === hourStartDate.getDate() &&
          d.getMinutes() >= r.startMin &&
          d.getMinutes() <= r.endMin
        );
      });
      return {
        value: idx, // 0..3
        label: r.label,
        disabled: !has,
        meta: r, // keep for later computation
      };
    });
    return opts;
  };

  const transformChartData = (apiData, selectedKey, tag, aggType) => {
    if (!selectedKey) return [];

    // ✅ RAW MODE — use direct timestamps and parameter values (no slots, no high/low)
    if (aggType === "RAW") {
      if (!apiData || !apiData.length) return [];

      const rawSeries = apiData.map((item) => {
        const ts =
          item.timestamp || item.TimeStamp || item.parameters?.TimeStamp;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        const val = item.parameters?.[selectedKey];

        return {
          x: date,
          y: val !== undefined ? Number(val) : null,
        };
      });

      // ✅ Base RAW graph line
      const series = [
        {
          name: `${selectedKey} (Raw)`,
          data: rawSeries,
          type: "line",
          color: "#007bff",
        },
      ];

      // ✅ Add condition lines for RAW mode
      if (tag?.conditions?.length) {
        tag.conditions.forEach((c) => {
          series.push({
            name: `Condition ${c.operator} ${c.value}`,
            data: rawSeries.map((d) => ({ x: d.x, y: Number(c.value) })),
            type: "line",
            color: "#ff4757",
            dashArray: 5,
          });
        });
      }

      return series;
    }

    const baseSlots = generateTimeSlots(
      aggType,
      pageWindow.start,
      pageWindow.end
    );

    const filledHigh = baseSlots.map((slot) => {
      const matchedItem = apiData?.find((itm) => {
        const ts =
          itm.highData?.[selectedKey]?.timestamp ||
          itm.lowData?.[selectedKey]?.timestamp;
        if (!ts) return false;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        switch (aggType) {
          case "YEAR":
            return date.getMonth() === slot.x.getMonth();
          case "MONTH":
            return date.getDate() === slot.x.getDate();
          case "DAY":
            return date.getHours() === slot.x.getHours();
          case "HOUR":
            // return date.getMinutes() === slot.x.getMinutes();
            const slotStart = slot.x;
            const slotEnd = new Date(slot.x.getTime() + 15 * 60 * 1000);
            return date >= slotStart && date < slotEnd;

          default:
            return false;
        }
      });

      return {
        x: slot.x,
        y: matchedItem
          ? matchedItem.highData?.[selectedKey]?.value ?? null
          : null,
      };
    });

    const filledLow = baseSlots.map((slot) => {
      const matchedItem = apiData?.find((itm) => {
        const ts =
          itm.highData?.[selectedKey]?.timestamp ||
          itm.lowData?.[selectedKey]?.timestamp;
        if (!ts) return false;

        const date =
          typeof ts === "number" && ts.toString().length === 10
            ? new Date(ts * 1000)
            : new Date(ts);

        switch (aggType) {
          case "YEAR":
            return date.getMonth() === slot.x.getMonth();
          case "MONTH":
            return date.getDate() === slot.x.getDate();
          case "DAY":
            return date.getHours() === slot.x.getHours();
          case "HOUR": {
            const slotStart = slot.x;
            const slotEnd = new Date(slot.x.getTime() + 15 * 60 * 1000);
            return date >= slotStart && date < slotEnd;
          }
          default:
            return false;
        }
      });

      return {
        x: slot.x,
        y: matchedItem
          ? matchedItem.lowData?.[selectedKey]?.value ?? null
          : null,
      };
    });

    const series = [
      {
        name: `${selectedKey} (High)`,
        data: filledHigh,
        type: "line",
        color: "#007bff",
      },
      {
        name: `${selectedKey} (Low)`,
        data: filledLow,
        type: "line",
        color: "#00b894",
      },
    ];

    if (tag?.conditions?.length) {
      tag.conditions.forEach((c) => {
        series.push({
          name: `Condition ${c.operator} ${c.value}`,
          data: baseSlots.map((d) => ({ x: d.x, y: Number(c.value) })),
          type: "line",
          color: "#ff4757",
          dashArray: 5,
        });
      });
    }

    return series;
  };

  const getChartOptions = (aggType) => {
    let xFormat;
    switch (aggType) {
      case "YEAR":
        xFormat = "MMM"; // Jan, Feb, Mar...
        break;
      case "MONTH":
        xFormat = "dd MMM"; // 01 Oct
        break;
      case "DAY":
        xFormat = "HH:mm"; // 10:30
        break;
      case "HOUR":
        xFormat = "mm"; // minute
        break;
      case "RAW":
        xFormat = "ss"; // seconds
        break;
      case "LIVE":
        xFormat = "HH:mm:ss";
        break;

      default:
        xFormat = "dd MMM HH:mm";
    }

    return {
      chart: {
        id: "rangeChart",
        type: "line",
        zoom: { enabled: false },
        toolbar: { show: true },
        animations: { enabled: true },

        events: {},
      },
      xaxis: {
        type: "datetime",
        labels: {
          datetimeUTC: false,
          formatter: (val, index, opts) => {
            const date = new Date(val);

            const pad = (n) => n.toString().padStart(2, "0");

            switch (aggType) {
              case "YEAR":
                return date.toLocaleString("en-IN", { month: "short" }); // Jan, Feb
              case "MONTH":
                return pad(date.getDate()); // 01, 02, 03...
              case "DAY": {
                const h = pad(date.getHours());
                const next = pad((date.getHours() + 1) % 24);
                return `${h}:00–${next}:00`;
              }
              case "HOUR": {
                const m = pad(date.getMinutes());
                const next = pad((date.getMinutes() + 15) % 60);
                return `${m}–${next} min`;
              }
              case "RAW": {
                const s = pad(date.getSeconds());
                const next = pad((date.getSeconds() + 5) % 60);
                // return `${s}–${next}`;
                return `${s} sec`;
              }
              case "LIVE":
                return date.toLocaleTimeString("en-IN", {
                  hour12: false,
                  hour: "2-digit",
                  minute: "2-digit",
                  second: "2-digit",
                });
              default:
                return date.toLocaleString("en-IN", {
                  day: "2-digit",
                  month: "short",
                });
            }
          },
        },
      },
      stroke: {
        curve: "smooth",
        width: 2,
      },
      markers: {
        size: 3,
        hover: { size: 5 },
        strokeWidth: 2, // keeps visibility strong
        strokeOpacity: 1,
        fillOpacity: 1,
        discrete: [],
      },
      tooltip: {
        x: { format: "dd MMM yyyy, HH:mm" },
      },
    };
  };

  const getValueFromPath = (obj, path) =>
    path?.split(".").reduce((acc, p) => acc?.[p], obj);

  const isConditionMet = (value, condition) => {
    const val = isNaN(value) ? value : Number(value);
    const condVal = isNaN(condition.value)
      ? condition.value
      : Number(condition.value);
    switch (condition.operator) {
      case "==":
        return val === condVal;
      case "!=":
        return val !== condVal;
      case ">":
        return val > condVal;
      case "<":
        return val < condVal;
      case ">=":
        return val >= condVal;
      case "<=":
        return val <= condVal;
      default:
        return false;
    }
  };

  const mappedKeySet = useMemo(
    () => new Set(tags.filter((t) => t?.key).map((t) => t.key)),
    [tags]
  );

  const alertKeySet = useMemo(() => {
    const s = new Set();
    for (const tag of tags) {
      const val = getValueFromPath(data, tag.key);
      if (tag.conditions?.some((c) => isConditionMet(val, c))) s.add(tag.key);
    }
    return s;
  }, [tags, data]);

  const filteredLeafNodes = useMemo(() => {
    if (!leafNodes) return [];
    switch (filterType) {
      case "Mapped":
        return leafNodes.filter((n) => mappedKeySet.has(n.key));
      case "Unmapped":
        return leafNodes.filter((n) => !mappedKeySet.has(n.key));
      case "Alert":
        return leafNodes.filter((n) => alertKeySet.has(n.key));
      default:
        return leafNodes;
    }
  }, [leafNodes, filterType, mappedKeySet, alertKeySet]);

  const parameterOptions = [
    {
      label: (
        <Tooltip title="All parameters">
          <MdFilterListOff />
        </Tooltip>
      ),
      value: "All",
    },
    {
      label: (
        <Tooltip title="Mapped parameters">
          <LuMapPinCheck />
        </Tooltip>
      ),
      value: "Mapped",
    },
    {
      label: (
        <Tooltip title="Unmapped parameters">
          <LuMapPinX />
        </Tooltip>
      ),
      value: "Unmapped",
    },
    {
      label: (
        <Tooltip title="Alert parameters">
          <IoWarningOutline />
        </Tooltip>
      ),
      value: "Alert",
    },
  ];

  /* ---------- Window Computation ---------- */

  const computeInitialWindow = (aggType, now = new Date()) => {
    switch (aggType) {
      case "RAW": {
        const start = new Date(now);
        start.setSeconds(0, 0);
        const end = new Date(start.getTime() + ONE_MIN_PAGE_MS);
        return { start, end };
      }
      case "HOUR": {
        const rounded = new Date(now);
        rounded.setMinutes(0, 0, 0);
        const start = new Date(rounded);
        const end = new Date(start.getTime() + 60 * 60 * 1000);
        return { start, end };
      }
      case "DAY": {
        const start = startOfToday(now);
        const end = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          23,
          59,
          59,
          999
        );
        return { start, end };
      }

      case "MONTH":
        return { start: startOfMonth(now), end: endOfMonth(now) };

      case "YEAR":
        return { start: startOfYear(now), end: endOfYear(now) };

      default:
        return { start: new Date(now - ONE_MIN_PAGE_MS), end: now };
    }
  };

  const shiftWindow = (aggType, current, dir) => {
    if (!current.start || !current.end) return null;
    const { start, end } = current;

    const add = (unitMs) => {
      return dir === "prev"
        ? { start: new Date(start - unitMs), end: new Date(end - unitMs) }
        : {
            start: new Date(start.getTime() + unitMs),
            end: new Date(end.getTime() + unitMs),
          };
    };

    switch (aggType) {
      case "YEAR": {
        const currentYear = start.getFullYear();
        const targetYear = dir === "prev" ? currentYear - 1 : currentYear + 1;

        return {
          start: new Date(targetYear, 0, 1, 0, 0, 0, 0),
          end: new Date(targetYear, 11, 31, 23, 59, 59, 999),
        };
      }
      case "MONTH":
        const base = new Date(start);
        const next =
          dir === "prev"
            ? new Date(base.getFullYear(), base.getMonth() - 1, 1)
            : new Date(base.getFullYear(), base.getMonth() + 1, 1);
        return { start: startOfMonth(next), end: endOfMonth(next) };
      case "DAY": {
        const cur = new Date(start);
        const target =
          dir === "prev"
            ? new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() - 1)
            : new Date(cur.getFullYear(), cur.getMonth(), cur.getDate() + 1);

        return {
          start: new Date(
            target.getFullYear(),
            target.getMonth(),
            target.getDate(),
            0,
            0,
            0,
            0
          ),
          end: new Date(
            target.getFullYear(),
            target.getMonth(),
            target.getDate(),
            23,
            59,
            59,
            999
          ),
        };
      }

      case "HOUR":
        return add(60 * 60 * 1000);
      // case "QUARTER":
      //   return add(15 * 60 * 1000);
      case "RAW":
        return add(60 * 1000);
      default:
        return null;
    }
  };

  /* ---------- API ---------- */
  const fetchHistorical = async ({ assetUuid, aggType, start, end }) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        assetUuid,
        aggregationType: aggType,
        start: toISOStringTrimmed(start),
        end: toISOStringTrimmed(end),
      });
      const res = await fetchHistoricalData(params);
      console.log("res.data", res.data);
      // if (aggType === "RAW") {
      //   setChartData(res.data.content || []);
      // } else {
      //   setChartData(res.data || []);
      // }
      setChartData(res.data || []);
      setPageWindow({ start, end });

      if (selectedKey) {
        const apiData =
          aggType === "RAW" ? res.data.content || [] : res.data || [];
      }

      // setChartData(res.data);
      setPageWindow({ start, end });
    } catch (e) {
      console.error("Fetch error:", e);
    } finally {
      setIsLoading(false);
    }
  };

  const loadInitialWindow = async (aggType, assetUuid) => {
    setAggregationType(aggType);
    const win = computeInitialWindow(aggType);

    setPageWindow({ start: win.start, end: win.end });

    if (aggType === "HOUR")
      rawInitialRef.current = {
        start: win.start.toISOString(),
        end: win.end.toISOString(),
      };

    await fetchHistorical({
      assetUuid,
      aggType,
      start: win.start,
      end: win.end,
    });
  };

  const onPrevious = async () => {
    const prev = shiftWindow(aggregationType, pageWindow, "prev");
    if (prev) {
      await fetchHistorical({
        assetUuid,
        aggType: aggregationType,
        start: prev.start,
        end: prev.end,
      });
      setPageWindow(prev); // ✅ update the window after successful call
    }
  };

  const onNext = async () => {
    const next = shiftWindow(aggregationType, pageWindow, "next");
    if (next) {
      await fetchHistorical({
        assetUuid,
        aggType: aggregationType,
        start: next.start,
        end: next.end,
      });
      setPageWindow(next); // ✅ update here too
    }
  };

  const historicalChartCall = (aggType) => {
    if (aggType === "LIVE") {
      setAggregationType("LIVE");
      setChartData([]);
      setPageWindow({ start: null, end: null });

      // ✅ CLEAR PREVIOUS LIVE POINTS WHEN ENTERING GL MODE
      setLiveSeries([]);
      return;
    }

    loadInitialWindow(aggType, assetUuid);
  };

  // Sort the filtered list alphabetically by displayName, then key
  const sortedLeafNodes = useMemo(() => {
    return [...filteredLeafNodes].sort((a, b) => {
      const tagA = tags.find((t) => t.key === a.key);
      const tagB = tags.find((t) => t.key === b.key);

      const nameA = tagA?.displayName?.trim() || null;
      const nameB = tagB?.displayName?.trim() || null;

      // If both have display names → sort alphabetically by displayName
      if (nameA && nameB) {
        return nameA.localeCompare(nameB, undefined, { sensitivity: "base" });
      }

      // If only one has displayName → that one comes first
      if (nameA && !nameB) return -1;
      if (!nameA && nameB) return 1;

      // If neither has displayName → sort alphabetically by key
      return a.key.localeCompare(b.key, undefined, { sensitivity: "base" });
    });
  }, [filteredLeafNodes, tags]);

  useEffect(() => {
    if (aggregationType !== "LIVE") return;
    if (!selectedKey) return;

    const val = getValueFromPath(data, selectedKey);
    const point = { x: new Date(), y: Number(val ?? null) };

    setLiveSeries((prev) => {
      const updated = [...prev, point];

      // ✅ Keep only latest 10 points
      if (updated.length > 10) {
        return updated.slice(updated.length - 10);
      }

      return updated;
    });
  }, [data, selectedKey, aggregationType]);

  useEffect(() => {
    // build selector options from chartData for currently selectedKey + aggregation
    const key = selectedKey;
    if (!key) {
      setSelectorOptions([]);
      setSelectorValue(null);
      return;
    }

    // extract timestamps from chartData using selectedKey
    const dates = extractTimestampsFromAggData(chartData, key);

    if (aggregationType === "YEAR") {
      const opts = buildYearOptions(dates);
      setSelectorOptions(opts.map((o) => ({ ...o, label: `${o.label}` })));
      setSelectorValue(null);
    } else if (aggregationType === "MONTH") {
      // need reference month: use pageWindow.start (which should be the month start)
      const ref = pageWindow.start ? new Date(pageWindow.start) : new Date();
      const opts = buildMonthOptions(dates, ref);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else if (aggregationType === "DAY") {
      const opts = buildDayOptions(dates);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else if (aggregationType === "HOUR") {
      // for HOUR, pageWindow.start is the hour start
      const ref = pageWindow.start ? new Date(pageWindow.start) : new Date();
      const opts = buildHourOptions(dates, ref);
      setSelectorOptions(opts);
      setSelectorValue(null);
    } else {
      setSelectorOptions([]);
      setSelectorValue(null);
    }
  }, [chartData, aggregationType, selectedKey, pageWindow.start]);

  /* ---------- Render ---------- */
  return (
    <div>
      <h2 style={{ paddingBottom: 16 }}>Parameter Graphs</h2>
      <Row gutter={16}>
        {/* LEFT PANEL */}
        <Col span={6}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            block
            options={parameterOptions}
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setSelectedKey(null);
            }}
            style={{ marginBottom: 16 }}
          />
          <List
            bordered
            // dataSource={filteredLeafNodes}
            dataSource={sortedLeafNodes.filter(
              (item) => item.key !== "timestamp"
            )}
            style={{ height: 500, overflowY: "auto" }}
            renderItem={(item) => {
              const isAlerting = alertKeySet.has(item.key);
              const isSelected = item.key === selectedKey;
              const tag = tags.find((t) => t.key === item.key);
              const val = getValueFromPath(data, item.key) ?? "-";
              return (
                <List.Item
                  // onClick={() => setSelectedKey(item.key)}
                  onClick={() => {
                    // 1️⃣ Clear old data
                    setLiveSeries([]);
                    setChartData([]);
                    setPageWindow({ start: null, end: null });
                    setSelectorOptions([]);
                    setSelectorValue(null);

                    // 2️⃣ Switch parameter
                    setSelectedKey(item.key);

                    // 3️⃣ If not LIVE, reload historical
                    if (aggregationType !== "LIVE") {
                      historicalChartCall(aggregationType);
                    }
                  }}
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? isAlerting
                        ? "rgba(255,50,50,0.2)"
                        : "#e6f7ff"
                      : "transparent",
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 12px",
                  }}
                >
                  {/* LEFT SIDE: Label + Key (Two Lines) */}
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <span style={{ fontWeight: 600 }}>
                      {tag?.displayName || "-"}
                    </span>
                    <span style={{ fontSize: 12, color: "#777" }}>
                      {item.key}
                    </span>
                  </div>

                  {/* RIGHT SIDE: Value */}
                  <div style={{ fontWeight: 500 }}>{String(val)}</div>
                </List.Item>
              );
            }}
          />
        </Col>

        {/* RIGHT PANEL */}
        <Col span={18}>
          <Card
            // title={selectedKey ? `Tag: ${selectedKey}` : "Select a parameter"}
            title={
              selectedKey && (
                <div style={{ padding: "1rem" }}>
                  <h3>
                    Tag:{" "}
                    {tags.find((t) => t.key === selectedKey)?.displayName ||
                      selectedKey}
                  </h3>
                </div>
              )
            }
            extra={
              <Radio.Group
                defaultValue="LIVE"
                optionType="button"
                value={aggregationType}
                buttonStyle="solid"
                disabled={selectedKey ? false : true}
                options={[
                  { label: "1Y", value: "YEAR" },
                  { label: "1M", value: "MONTH" },
                  { label: "1D", value: "DAY" },
                  { label: "1H", value: "HOUR" },
                  { label: "1Min", value: "RAW" },
                  {
                    label: <Tooltip title="Go Live">GL</Tooltip>,
                    value: "LIVE",
                  },
                ]}
                onChange={(e) => historicalChartCall(e.target.value)}
              />
            }
            style={{ minHeight: 350 }}
          >
            {selectedKey ? (
              <>
                <div
                  style={{
                    textAlign: "center",
                    marginBottom: 10,
                    fontWeight: 500,
                  }}
                >
                  {aggregationType === "LIVE"
                    ? `${formatWindowLabel(
                        "LIVE",
                        { liveValue: getValueFromPath(data, selectedKey) }, // ✅ inject value
                        null
                      )} = ${getValueFromPath(data, selectedKey)}`
                    : formatWindowLabel(
                        aggregationType,
                        pageWindow.start,
                        pageWindow.end
                      )}
                  {/* Put this right under the formatted window label */}
                  {selectedKey && selectorOptions.length > 0 && (
                    <div style={{ textAlign: "center", marginBottom: 10 }}>
                      <Select
                        value={selectorValue}
                        onChange={async (val) => {
                          setSelectorValue(val);
                          const opt = selectorOptions.find(
                            (o) => o.value === val
                          );

                          let nextAgg = aggregationType;
                          let newStart = null;
                          let newEnd = null;

                          // --- 1️⃣ YEAR → MONTH ---
                          if (aggregationType === "YEAR") {
                            const year = pageWindow.start
                              ? new Date(pageWindow.start).getFullYear()
                              : new Date().getFullYear();
                            const m = Number(val);
                            newStart = new Date(year, m, 1, 0, 0, 0, 0);
                            newEnd = endOfMonth(newStart);
                            nextAgg = "MONTH";
                          }

                          // --- 2️⃣ MONTH → DAY ---
                          else if (aggregationType === "MONTH") {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = Number(val);
                            newStart = new Date(year, month, day, 0, 0, 0, 0);
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              23,
                              59,
                              59,
                              999
                            );
                            nextAgg = "DAY";
                          }

                          // --- 3️⃣ DAY → HOUR ---
                          else if (aggregationType === "DAY") {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = base.getDate();
                            const hour = Number(val);
                            newStart = new Date(
                              year,
                              month,
                              day,
                              hour,
                              0,
                              0,
                              0
                            );
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              hour + 1,
                              0,
                              0,
                              0
                            );
                            nextAgg = "HOUR";
                          }

                          // --- 4️⃣ HOUR → RAW (15-min slot) ---
                          else if (aggregationType === "HOUR" && opt?.meta) {
                            const base = pageWindow.start
                              ? new Date(pageWindow.start)
                              : new Date();
                            const year = base.getFullYear();
                            const month = base.getMonth();
                            const day = base.getDate();
                            const hour = base.getHours();

                            const { startMin, endMin } = opt.meta;
                            newStart = new Date(
                              year,
                              month,
                              day,
                              hour,
                              startMin,
                              0,
                              0
                            );
                            newEnd = new Date(
                              year,
                              month,
                              day,
                              hour,
                              startMin,
                              59,
                              999
                            );
                            nextAgg = "RAW";
                          }

                          if (newStart && newEnd && nextAgg) {
                            setAggregationType(nextAgg);
                            await fetchHistorical({
                              assetUuid,
                              aggType: nextAgg,
                              start: newStart,
                              end: newEnd,
                            });
                          }
                        }}
                        style={{ width: 360 }}
                        placeholder={
                          aggregationType === "YEAR"
                            ? "Select month"
                            : aggregationType === "MONTH"
                            ? "Select date"
                            : aggregationType === "DAY"
                            ? "Select hour"
                            : aggregationType === "HOUR"
                            ? "Select 15-min range"
                            : "Select"
                        }
                      >
                        {selectorOptions.map((o) => (
                          <Option
                            key={String(o.value)}
                            value={o.value}
                            disabled={o.disabled}
                          >
                            {o.label}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  )}
                </div>

                <ReactApexChart
                  options={{
                    ...getChartOptions(aggregationType),
                    type: "line",
                    tooltip: { shared: true, intersect: false },
                    stroke: { curve: "smooth", width: 2 },
                  }}
                  series={
                    aggregationType === "LIVE"
                      ? [
                          {
                            name: `${selectedKey} (Live)`,
                            data: liveSeries,
                            type: "line",
                          },
                          // ✅ Add condition lines for GL
                          ...(tags
                            .find((t) => t.key === selectedKey)
                            ?.conditions?.map((c) => ({
                              name: `Condition ${c.operator} ${c.value}`,
                              data: liveSeries.map((d) => ({
                                x: d.x,
                                y: Number(c.value),
                              })),
                              type: "line",
                              color: "#ff4757",
                              dashArray: 5,
                            })) || []),
                        ]
                      : transformChartData(
                          chartData,
                          selectedKey,
                          tags.find((t) => t.key === selectedKey),
                          aggregationType
                        )
                  }
                  type="line"
                  height={320}
                />

                {aggregationType !== "LIVE" && (
                  <div style={{ marginTop: 10, textAlign: "center" }}>
                    <Button onClick={onPrevious} style={{ marginRight: 8 }}>
                      Previous
                    </Button>
                    <Button onClick={onNext}>Next</Button>
                  </div>
                )}
              </>
            ) : (
              <p>Select a parameter from the left to view its graph.</p>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LiveMonitoring;
